import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar as CalendarIcon, User, Mail, Phone, MessageSquare, Clock, Search, Car, MapPin, Users, Plane, ArrowRight, ArrowLeftRight, X, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR, enUS, es, fr } from 'date-fns/locale';
import axios from 'axios';
import { Calendar } from './ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';
import { trackBooking } from '../utils/analytics';
import { getDirectionalPrice, getRoundTripSavings, searchTransfers } from '../data/airportTransfers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const dateLocaleMap = {
  pt: ptBR,
  en: enUS,
  es: es,
  fr: fr,
};

export const BookingSectionNew = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = dateLocaleMap[i18n.language] || enUS;

  const [date, setDate] = useState();
  const [serviceType, setServiceType] = useState(''); // 'transfer' or 'tour'
  const [transferDirection, setTransferDirection] = useState('from_airport'); // 'from_airport' | 'to_airport' | 'round_trip'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    destination: '',
    time: '',
    passengers: '1',
    message: ''
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const searchRef = useRef(null);
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      const results = searchTransfers(searchQuery);
      setSearchResults(results);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (serviceType === 'transfer' && selectedTransfer && formData.passengers) {
      const price = getDirectionalPrice(selectedTransfer.id, formData.passengers, transferDirection);
      setCalculatedPrice(price);
    } else {
      setCalculatedPrice(null);
    }
  }, [serviceType, selectedTransfer, formData.passengers, transferDirection]);

  const handleServiceTypeChange = (type) => {
    setServiceType(type);
    setFormData(prev => ({ ...prev, service: '', destination: '' }));
    setSelectedTransfer(null);
    setCalculatedPrice(null);
    setSearchQuery('');
    setTransferDirection('from_airport');
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSelectedTransfer(null);
    setCalculatedPrice(null);
    setShowResults(false);
    setFormData(prev => ({ ...prev, service: '', destination: '' }));
  };

  const handleTransferSelect = (transfer) => {
    setSelectedTransfer(transfer);
    setSearchQuery(transfer.name);
    setFormData(prev => ({ 
      ...prev, 
      service: `transfer-${transfer.id}`,
      destination: transfer.name 
    }));
    setShowResults(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error(t('booking.errors.no_date'));
      return;
    }
    if (!serviceType) {
      toast.error(t('booking.errors.no_type'));
      return;
    }
    if (serviceType === 'transfer' && !selectedTransfer) {
      toast.error(t('booking.errors.no_destination'));
      return;
    }
    if (serviceType === 'tour' && !formData.service) {
      toast.error(t('booking.errors.no_tour'));
      return;
    }

    setIsSubmitting(true);

    try {
      const bookingData = {
        ...formData,
        date: format(date, 'yyyy-MM-dd'),
        serviceType: serviceType,
        calculatedPrice: calculatedPrice,
        language: i18n.language || 'en',
        direction: serviceType === 'transfer' ? transferDirection : null,
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      
      if (response.data) {
        trackBooking(formData.service, formData.passengers);
        
        toast.success(t('booking.success_title'), {
          description: t('booking.success_desc', { id: response.data.id.substring(0, 8) })
        });
        
        setFormData({
          name: '', email: '', phone: '', service: '',
          destination: '', time: '', passengers: '1', message: ''
        });
        setDate(undefined);
        setServiceType('');
        setSelectedTransfer(null);
        setCalculatedPrice(null);
        setSearchQuery('');
        setTransferDirection('from_airport');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(t('booking.errors.submit_error'), {
        description: t('booking.errors.submit_error_desc')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="booking-title">
            {t('booking.title')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('booking.subtitle')}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card className="shadow-2xl border-0">
            <CardHeader className="bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-t-lg">
              <CardTitle className="text-2xl font-bold text-center">
                {t('booking.card_title')}
              </CardTitle>
            </CardHeader>
            
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Service Type Selection */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <button
                    type="button"
                    onClick={() => handleServiceTypeChange('transfer')}
                    className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                      serviceType === 'transfer'
                        ? 'border-amber-600 bg-amber-50 shadow-lg scale-105'
                        : 'border-gray-200 hover:border-amber-300 hover:bg-gray-50'
                    }`}
                    data-testid="service-type-transfer"
                  >
                    <Car className={`w-12 h-12 mx-auto mb-3 ${serviceType === 'transfer' ? 'text-amber-600' : 'text-gray-400'}`} />
                    <h3 className="text-lg font-bold text-gray-900">{t('booking.type_transfer')}</h3>
                    <p className="text-sm text-gray-600 mt-2">{t('booking.type_transfer_desc')}</p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => handleServiceTypeChange('tour')}
                    className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                      serviceType === 'tour'
                        ? 'border-amber-600 bg-amber-50 shadow-lg scale-105'
                        : 'border-gray-200 hover:border-amber-300 hover:bg-gray-50'
                    }`}
                    data-testid="service-type-tour"
                  >
                    <MapPin className={`w-12 h-12 mx-auto mb-3 ${serviceType === 'tour' ? 'text-amber-600' : 'text-gray-400'}`} />
                    <h3 className="text-lg font-bold text-gray-900">{t('booking.type_tour')}</h3>
                    <p className="text-sm text-gray-600 mt-2">{t('booking.type_tour_desc')}</p>
                  </button>
                </div>

                {/* Conditional Fields based on Service Type */}
                {serviceType && (
                  <>
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Name */}
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-gray-700 font-medium flex items-center">
                          <User className="w-4 h-4 mr-2" />
                          {t('booking.name')} *
                        </Label>
                        <Input
                          id="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder={t('booking.name_placeholder')}
                          className="border-gray-300"
                          data-testid="booking-name-input"
                        />
                      </div>

                      {/* Email */}
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-gray-700 font-medium flex items-center">
                          <Mail className="w-4 h-4 mr-2" />
                          {t('booking.email')} *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder={t('booking.email_placeholder')}
                          className="border-gray-300"
                          data-testid="booking-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Phone */}
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-gray-700 font-medium flex items-center">
                          <Phone className="w-4 h-4 mr-2" />
                          {t('booking.phone')} *
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          placeholder={t('booking.phone_placeholder')}
                          className="border-gray-300"
                          data-testid="booking-phone-input"
                        />
                      </div>

                      {/* Passengers */}
                      <div className="space-y-2">
                        <Label htmlFor="passengers" className="text-gray-700 font-medium flex items-center">
                          <Users className="w-4 h-4 mr-2" />
                          {t('booking.passengers')} *
                        </Label>
                        <Select value={formData.passengers} onValueChange={(value) => handleInputChange('passengers', value)}>
                          <SelectTrigger className="border-gray-300" data-testid="booking-passengers-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {serviceType === 'transfer' ? (
                              <>
                                {[1, 2, 3].map(num => (
                                  <SelectItem key={num} value={num.toString()}>
                                    {num} {num === 1 ? t('booking.passenger_singular') : t('booking.passenger_plural')} ({t('booking.vehicle_3pax')})
                                  </SelectItem>
                                ))}
                                {[4, 5, 6, 7].map(num => (
                                  <SelectItem key={num} value={num.toString()}>
                                    {num} {t('booking.passenger_plural')} ({t('booking.vehicle_4to7pax')})
                                  </SelectItem>
                                ))}
                              </>
                            ) : (
                              <>
                                {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                                  <SelectItem key={num} value={num.toString()}>
                                    {num} {num === 1 ? t('booking.passenger_singular') : t('booking.passenger_plural')}
                                  </SelectItem>
                                ))}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Transfer Destination Search OR Tour Selection */}
                    {serviceType === 'transfer' ? (
                      <>
                        {/* DIRECTION SELECTOR — From Airport / To Airport / Round Trip */}
                        <div className="space-y-3">
                          <Label className="text-gray-700 font-medium flex items-center">
                            <ArrowLeftRight className="w-4 h-4 mr-2" />
                            {t('booking.direction_label')} *
                          </Label>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <button
                              type="button"
                              onClick={() => setTransferDirection('from_airport')}
                              className={`relative p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                                transferDirection === 'from_airport'
                                  ? 'border-amber-600 bg-amber-50 shadow-md'
                                  : 'border-gray-200 hover:border-amber-300 bg-white'
                              }`}
                              data-testid="direction-from-airport"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <Plane className={`w-5 h-5 ${transferDirection === 'from_airport' ? 'text-amber-600' : 'text-gray-400'}`} />
                                <ArrowRight className="w-4 h-4 text-gray-400" />
                                <MapPin className={`w-5 h-5 ${transferDirection === 'from_airport' ? 'text-amber-600' : 'text-gray-400'}`} />
                              </div>
                              <p className="text-sm font-bold text-gray-900">{t('booking.direction_from_airport')}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{t('booking.direction_from_airport_desc')}</p>
                            </button>

                            <button
                              type="button"
                              onClick={() => setTransferDirection('to_airport')}
                              className={`relative p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                                transferDirection === 'to_airport'
                                  ? 'border-amber-600 bg-amber-50 shadow-md'
                                  : 'border-gray-200 hover:border-amber-300 bg-white'
                              }`}
                              data-testid="direction-to-airport"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <MapPin className={`w-5 h-5 ${transferDirection === 'to_airport' ? 'text-amber-600' : 'text-gray-400'}`} />
                                <ArrowRight className="w-4 h-4 text-gray-400" />
                                <Plane className={`w-5 h-5 ${transferDirection === 'to_airport' ? 'text-amber-600' : 'text-gray-400'}`} />
                              </div>
                              <p className="text-sm font-bold text-gray-900">{t('booking.direction_to_airport')}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{t('booking.direction_to_airport_desc')}</p>
                            </button>

                            <button
                              type="button"
                              onClick={() => setTransferDirection('round_trip')}
                              className={`relative p-4 rounded-xl border-2 transition-all duration-200 text-left overflow-hidden ${
                                transferDirection === 'round_trip'
                                  ? 'border-green-600 bg-green-50 shadow-md'
                                  : 'border-gray-200 hover:border-green-400 bg-white'
                              }`}
                              data-testid="direction-round-trip"
                            >
                              <span className="absolute top-1.5 right-1.5 bg-green-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide">-10%</span>
                              <div className="flex items-center gap-2 mb-2">
                                <Plane className={`w-5 h-5 ${transferDirection === 'round_trip' ? 'text-green-600' : 'text-gray-400'}`} />
                                <ArrowLeftRight className="w-4 h-4 text-gray-400" />
                                <MapPin className={`w-5 h-5 ${transferDirection === 'round_trip' ? 'text-green-600' : 'text-gray-400'}`} />
                              </div>
                              <p className="text-sm font-bold text-gray-900">{t('booking.direction_round_trip')}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{t('booking.direction_round_trip_desc')}</p>
                            </button>
                          </div>
                        </div>

                        {/* SEARCH FIELD with CLEAR BUTTON */}
                        <div className="space-y-2" ref={searchRef}>
                          <Label htmlFor="destination" className="text-gray-700 font-medium flex items-center">
                            <Search className="w-4 h-4 mr-2" />
                            {t('booking.destination')} *
                          </Label>
                          <div className="relative">
                            <Input
                              id="destination"
                              type="text"
                              value={searchQuery}
                              onChange={(e) => {
                                setSearchQuery(e.target.value);
                                if (selectedTransfer && e.target.value !== selectedTransfer.name) {
                                  setSelectedTransfer(null);
                                  setCalculatedPrice(null);
                                  setFormData(prev => ({ ...prev, service: '', destination: '' }));
                                }
                              }}
                              onFocus={() => {
                                if (searchQuery.length >= 2) setShowResults(true);
                              }}
                              placeholder={t('booking.search_placeholder')}
                              className="border-gray-300 pl-10 pr-10"
                              autoComplete="off"
                              data-testid="booking-destination-search"
                            />
                            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                            {searchQuery && (
                              <button
                                type="button"
                                onClick={handleClearSearch}
                                aria-label={t('booking.clear_search')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                                data-testid="clear-search-btn"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}

                            {showResults && searchResults.length > 0 && (
                              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                {searchResults.map((transfer) => (
                                  <button
                                    key={transfer.id}
                                    type="button"
                                    onClick={() => handleTransferSelect(transfer)}
                                    className="w-full px-4 py-3 text-left hover:bg-amber-50 border-b border-gray-100 last:border-0 transition-colors"
                                    data-testid={`transfer-option-${transfer.id}`}
                                  >
                                    <div className="flex justify-between items-center">
                                      <div>
                                        <p className="font-medium text-gray-900">{transfer.name}</p>
                                        <p className="text-sm text-gray-500">{transfer.km} km • {transfer.country}</p>
                                      </div>
                                      <div className="text-right">
                                        <p className="text-sm font-semibold text-amber-600">€{transfer.price3pax} - €{transfer.price4to7pax}</p>
                                        <p className="text-xs text-gray-500">{t('booking.search_pax_label')}</p>
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* TRIP SUMMARY CARD — premium visual route + total + savings */}
                          {selectedTransfer && calculatedPrice != null && (
                            <div className="mt-4 rounded-xl overflow-hidden border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50 shadow-md animate-in fade-in slide-in-from-bottom-2 duration-300">
                              <div className="px-5 py-3 bg-gradient-to-r from-amber-600 to-amber-700 text-white flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide">
                                  <Sparkles className="w-4 h-4" />
                                  {t('booking.trip_summary')}
                                </div>
                                <div className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                                  {transferDirection === 'round_trip' ? t('booking.round_trip') : t('booking.one_way')}
                                </div>
                              </div>

                              <div className="p-5 space-y-4">
                                {/* Visual route */}
                                <div className="flex items-center justify-between">
                                  {/* From */}
                                  <div className="flex flex-col items-center text-center flex-1">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-md ${
                                      transferDirection === 'to_airport' ? 'bg-gradient-to-br from-amber-100 to-amber-200' : 'bg-gradient-to-br from-amber-500 to-amber-600'
                                    }`}>
                                      {transferDirection === 'to_airport'
                                        ? <MapPin className="w-6 h-6 text-amber-700" />
                                        : <Plane className="w-6 h-6 text-white" />}
                                    </div>
                                    <p className="text-xs font-semibold text-gray-700 mt-2">
                                      {transferDirection === 'to_airport' ? selectedTransfer.name : 'Porto Airport'}
                                    </p>
                                  </div>

                                  {/* Connector */}
                                  <div className="flex-1 flex items-center justify-center px-2">
                                    {transferDirection === 'round_trip' ? (
                                      <div className="w-full flex items-center justify-center">
                                        <div className="flex-1 border-t-2 border-dashed border-amber-400"></div>
                                        <ArrowLeftRight className="w-5 h-5 text-amber-600 mx-2" />
                                        <div className="flex-1 border-t-2 border-dashed border-amber-400"></div>
                                      </div>
                                    ) : (
                                      <div className="w-full flex items-center justify-center">
                                        <div className="flex-1 border-t-2 border-dashed border-amber-400"></div>
                                        <ArrowRight className="w-5 h-5 text-amber-600 mx-2" />
                                        <div className="flex-1 border-t-2 border-dashed border-amber-400"></div>
                                      </div>
                                    )}
                                  </div>

                                  {/* To */}
                                  <div className="flex flex-col items-center text-center flex-1">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-md ${
                                      transferDirection === 'to_airport' ? 'bg-gradient-to-br from-amber-500 to-amber-600' : 'bg-gradient-to-br from-amber-100 to-amber-200'
                                    }`}>
                                      {transferDirection === 'to_airport'
                                        ? <Plane className="w-6 h-6 text-white" />
                                        : <MapPin className="w-6 h-6 text-amber-700" />}
                                    </div>
                                    <p className="text-xs font-semibold text-gray-700 mt-2">
                                      {transferDirection === 'to_airport' ? 'Porto Airport' : selectedTransfer.name}
                                    </p>
                                  </div>
                                </div>

                                {/* Meta row: distance + pax */}
                                <div className="flex items-center justify-center gap-4 text-xs text-gray-600 border-t border-amber-200 pt-3">
                                  <span>📍 {selectedTransfer.km} km</span>
                                  <span>•</span>
                                  <span>👥 {parseInt(formData.passengers) <= 3 ? '1-3' : '4-7'} {t('booking.passengers_short')}</span>
                                  <span>•</span>
                                  <span>{selectedTransfer.country}</span>
                                </div>

                                {/* Total + savings */}
                                <div className="flex items-end justify-between bg-white rounded-lg px-4 py-3 shadow-sm border border-amber-100">
                                  <div>
                                    <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">{t('booking.total_price')}</p>
                                    <p className="text-3xl font-bold text-amber-700">€{calculatedPrice}</p>
                                  </div>
                                  {transferDirection === 'round_trip' && (
                                    <div className="text-right bg-green-50 border border-green-200 rounded-md px-3 py-1.5">
                                      <p className="text-[10px] uppercase tracking-wide text-green-700 font-bold">-10%</p>
                                      <p className="text-sm font-bold text-green-700">
                                        {t('booking.round_trip_save', { amount: getRoundTripSavings(selectedTransfer.id, formData.passengers) })}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="space-y-2">
                        <Label htmlFor="tour" className="text-gray-700 font-medium flex items-center">
                          <MapPin className="w-4 h-4 mr-2" />
                          {t('booking.tour_label')} *
                        </Label>
                        <Select value={formData.service} onValueChange={(value) => handleInputChange('service', value)}>
                          <SelectTrigger className="border-gray-300" data-testid="booking-tour-select">
                            <SelectValue placeholder={t('booking.tour_placeholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="braga">{t('booking.tours.braga')}</SelectItem>
                            <SelectItem value="guimaraes">{t('booking.tours.guimaraes')}</SelectItem>
                            <SelectItem value="santiago">{t('booking.tours.santiago')}</SelectItem>
                            <SelectItem value="aveiro">{t('booking.tours.aveiro')}</SelectItem>
                            <SelectItem value="fatima">{t('booking.tours.fatima')}</SelectItem>
                            <SelectItem value="nazare">{t('booking.tours.nazare')}</SelectItem>
                            <SelectItem value="douro">{t('booking.tours.douro')}</SelectItem>
                            <SelectItem value="paiva">{t('booking.tours.paiva')}</SelectItem>
                            <SelectItem value="custom">{t('booking.tours.custom')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Date */}
                      <div className="space-y-2">
                        <Label className="text-gray-700 font-medium flex items-center">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {t('booking.date')} *
                        </Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full justify-start text-left font-normal border-gray-300 ${!date && 'text-gray-500'}`}
                              data-testid="booking-date-trigger"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {date ? format(date, 'PPP', { locale: dateLocale }) : t('booking.date_placeholder')}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={date}
                              onSelect={setDate}
                              disabled={(d) => d < new Date()}
                              initialFocus
                              locale={dateLocale}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Time */}
                      <div className="space-y-2">
                        <Label htmlFor="time" className="text-gray-700 font-medium flex items-center">
                          <Clock className="w-4 h-4 mr-2" />
                          {t('booking.time')}
                        </Label>
                        <Input
                          id="time"
                          type="time"
                          value={formData.time}
                          onChange={(e) => handleInputChange('time', e.target.value)}
                          className="border-gray-300"
                          data-testid="booking-time-input"
                        />
                      </div>
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-gray-700 font-medium flex items-center">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        {t('booking.message')}
                      </Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        placeholder={t('booking.message_placeholder')}
                        rows={4}
                        className="border-gray-300 resize-none"
                        data-testid="booking-message-input"
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4">
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                        data-testid="booking-submit-btn"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3" />
                            {t('booking.processing')}
                          </>
                        ) : (
                          <>
                            {t('booking.submit')}
                            {calculatedPrice && <span className="ml-3 font-bold">• €{calculatedPrice}</span>}
                          </>
                        )}
                      </Button>
                      <p className="text-center text-sm text-gray-500 mt-4">
                        {t('booking.footer_note')}
                      </p>
                    </div>
                  </>
                )}

                {!serviceType && (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-lg">👆 {t('booking.select_first')}</p>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
